/**
 * CreditsPill — Sprint 1070.C5. Shared credit-balance pill rendered by
 * `<StudioShell>` in every studio's header row (right-aligned, inline
 * with the child mission breadcrumb). Mirrors Creative Studio's
 * `cs-credit-balance` button styling so the family reads as one product.
 *
 * Self-contained — fetches `/api/ai-credits/status` on mount, polls every
 * 60s, and routes to `/billing` on click. No props required.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";
import { api } from "../../lib/api";

export default function CreditsPill() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCredits = async () => {
      try {
        const r = await api.get("/ai-credits/status");
        if (!cancelled) setCredits(r.data?.credit_balance ?? null);
      } catch { /* graceful — pill just doesn't render */ }
    };
    fetchCredits();
    const id = setInterval(fetchCredits, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (credits === null) return null;
  const lowCredits = credits <= 50;

  return (
    <button
      onClick={() => navigate("/platform/ai-credits")}
      data-testid="studio-shell-credits-pill"
      title="Click to manage credits"
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        lowCredits
          ? "bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20"
          : "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20"
      }`}
    >
      <Zap className="w-3 h-3" />
      <span>{credits.toLocaleString()} credits</span>
      {lowCredits && <span className="text-[10px] uppercase font-bold ml-1">low</span>}
    </button>
  );
}
